import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { LayoutChangeEvent, ScrollView, View } from 'react-native'

import styled from 'styled-components/native'

import { ProviderInsightsSubAreaName } from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { SkillsGlossaryEntry } from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/skillsGlossary'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { Link } from '../../atoms/link/Link'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Title } from '../../atoms/title/Title'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Modal } from '../modal/Modal'

export interface SkillsGlossarySidePanelProps {
  visible: boolean
  onClose: () => void
  entries: SkillsGlossaryEntry[]
  /** Skill to scroll to and highlight when the panel opens (e.g. the underlined term the user clicked). */
  activeSubAreaName?: ProviderInsightsSubAreaName
  testIDPrefix?: string
}

const Container = styled(ScrollView)(({ theme }: { theme: ThemeType }) => ({
  flex: 1,
  paddingTop: theme.spacing['32px'],
  paddingRight: theme.spacing['32px'],
  paddingBottom: theme.spacing['48px'],
  paddingLeft: theme.spacing['32px'],
}))

const HeaderRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing['24px'],
}))

const Content = styled(View)(({ theme }: { theme: ThemeType }) => ({
  width: '100%',
  gap: theme.spacing['16px'],
}))

const EntryContainer = styled(View)<{ isActive: boolean }>(
  ({ theme, isActive }: { theme: ThemeType; isActive: boolean }) => ({
    gap: theme.spacing['4px'],
    padding: theme.spacing['12px'],
    borderRadius: theme.spacing['8px'],
    backgroundColor: isActive ? theme.colors.backgroundHighlightPrimary : 'transparent',
  }),
)

const DefinitionText = styled(BodyText)(({ theme }: { theme: ThemeType }) => ({
  color: theme.colors.textSecondary,
}))

export const SkillsGlossarySidePanel: FunctionComponent<SkillsGlossarySidePanelProps> = ({
  visible,
  onClose,
  entries,
  activeSubAreaName,
  testIDPrefix = 'SkillsGlossarySidePanel',
}) => {
  const { formatMessage } = useIntl()
  const scrollViewRef = useRef<ScrollView>(null)
  // Each entry's layout.y is measured from the Content block, but scrollTo needs a position in the ScrollView's content,
  // so the Content block's own offset (the header row and its margin sit above it) is added to the entry's offset.
  const entryOffsets = useRef<Partial<Record<ProviderInsightsSubAreaName, number>>>({})
  const contentOffset = useRef<number | undefined>(undefined)

  const scrollToEntry = useCallback((subAreaName: ProviderInsightsSubAreaName) => {
    const entryOffset = entryOffsets.current[subAreaName]
    const baseOffset = contentOffset.current
    if (entryOffset !== undefined && baseOffset !== undefined) {
      scrollViewRef.current?.scrollTo({ y: baseOffset + entryOffset, animated: true })
    }
  }, [])

  // Covers the case where the entry has already been laid out (panel content stays mounted between opens). Opening
  // without a skill (the header button) resets to the top, since the ScrollView keeps its last position between opens.
  useEffect(() => {
    if (!visible) {
      return
    }
    if (activeSubAreaName) {
      scrollToEntry(activeSubAreaName)
    } else {
      scrollViewRef.current?.scrollTo({ y: 0, animated: false })
    }
  }, [visible, activeSubAreaName, scrollToEntry])

  // On the first open the entries and the Content block lay out after the effect above has already run, and the order of
  // those layout events is not guaranteed. Both handlers try to scroll; it only happens once both offsets are known, so
  // whichever event arrives last triggers it with correct values.
  const onEntryLayout = (subAreaName: ProviderInsightsSubAreaName) => (event: LayoutChangeEvent) => {
    entryOffsets.current[subAreaName] = event.nativeEvent.layout.y
    if (visible && subAreaName === activeSubAreaName) {
      scrollToEntry(subAreaName)
    }
  }

  const onContentLayout = (event: LayoutChangeEvent) => {
    contentOffset.current = event.nativeEvent.layout.y
    if (visible && activeSubAreaName) {
      scrollToEntry(activeSubAreaName)
    }
  }

  return (
    <Modal
      visible={visible}
      onRequestClose={onClose}
      isSidePanel
      showCloseIcon={false}
      accessibilityLabel={formatMessage({
        defaultMessage: 'Skills glossary panel',
        description: 'Accessibility label for the skills glossary side panel in Care Insights',
      })}
      modalContents={
        <Container ref={scrollViewRef}>
          <HeaderRow>
            <Title
              size={TitleSize.SMALL}
              text={formatMessage({
                defaultMessage: 'Skills glossary',
                description: 'Title for the skills glossary side panel in Care Insights',
              })}
            />
            <TertiaryIconButton
              testID={tID(`${testIDPrefix}-close`)}
              leftIcon={<XIcon />}
              onPress={onClose}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close',
                description: 'Accessibility label for close button',
              })}
              style={{ backgroundColor: 'transparent', padding: 0 }}
            />
          </HeaderRow>
          <Content onLayout={onContentLayout}>
            {entries.map(({ subAreaName, term, definition, learnMoreUrl }) => (
              <EntryContainer
                key={subAreaName}
                isActive={subAreaName === activeSubAreaName}
                onLayout={onEntryLayout(subAreaName)}
                testID={tID(`${testIDPrefix}-entry-${subAreaName}`)}
              >
                <BodyText size={BodyTextSize.SMALL} text={term} bold />
                <DefinitionText
                  size={BodyTextSize.SMALL}
                  text={
                    <>
                      {definition}
                      {learnMoreUrl && (
                        <>
                          {' '}
                          <Link
                            text={formatMessage({
                              defaultMessage: 'Learn more',
                              description: 'Link to a related help article in the skills glossary',
                            })}
                            onPress={() => openUrl(learnMoreUrl)}
                            size={BodyTextSize.SMALL}
                            testID={tID(`${testIDPrefix}-learnMore-${subAreaName}`)}
                          />
                        </>
                      )}
                    </>
                  }
                />
              </EntryContainer>
            ))}
          </Content>
        </Container>
      }
      customSidePanelStyle={{
        padding: '0 0 0 0',
        width: '452px',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      }}
    />
  )
}
